
import Redactor from '@/plugins/redactor/redactor.usm.min';
import handle from '@/plugins/handle.redactor'; // DO NOT REMOVE

export default {
  name: 'RedactorInput',
  props: {
    model_type: {
      type: String,
      required: true,
    },
    model_uuid: {
      type: String,
      required: true,
    },
    handle_type: {
      type: String,
      required: false,
      default: 'note',
    },
    value: {
      type: String,
      required: false,
    },
    setRef: {
      type: String,
      required: false,
      default: function () {
        return 'editor-' + this.model_type + '-' + this.model_uuid + '-' + this.handle_type;
      },
    },
    hasFocus: {
      type: Boolean,
      default: false,
    },
    // Optional static mention list, shape { [uuid]: { item, replacement } }, used instead of the endpoint.
    items: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data: () => {
    return {
      url: '',
      redactor: null,
      editorElement: null,
      airToolbarEl: null,
    };
  },

  head() {
    return {
      link: [
        {
          rel: 'stylesheet',
          href: '/handle.min.css',
        },
      ],
    };
  },

  mounted() {
    this.buildRedactor();
    this.editorElement = this.$refs[this.setRef];
    this.buildRedactor();
  },

  beforeDestroy() {
    if (this.airToolbarEl && this.airToolbarEl.parentElement === document.body) {
      this.airToolbarEl.remove();
    }
  },

  methods: {
    clearContents() {
      Redactor(this.editorElement, 'source.setCode', '<p></p>');
    },
    buildRedactor() {
      switch (this.handle_type) {
        case 'note':
          this.url = '/note/' + this.model_type + '/' + this.model_uuid + '/mentions';
          break;
        case 'conversation':
          this.url = '/conversation/' + this.model_uuid + '/mentions';
          break;
      }

      const self = this;

      Redactor(this.editorElement, 'destroy');
      this.airToolbarEl = null;
      Redactor(this.editorElement, {
        focus: this.hasFocus,
        air: true,
        shortcodes: false,
        plugins: ['handle'],
        buttons: ['bold', 'italic', 'deleted', 'lists', 'link'],
        handle: this.$nuxt.$api.host + this.url,
        // A function so each lookup reads the latest items without rebuilding (and clearing) the editor.
        handleItems: this.items ? () => self.items : undefined,
        callbacks: {
          blur: (e) => {
            this.$emit('blur');
          },
          changed: (e) => {
            let content = Redactor(this.editorElement, 'source.getCode');
            this.$emit('input', content);
          },
          airOpened: () => {
            this.$nextTick(this.positionAirToolbar);
          },
          dropdown: {
            opened: (e, dropdown, btn) => {
              this.$nextTick(() => {
                this.patchListDropdown(dropdown);
                this.positionDropdown(dropdown, btn);
              });
            },
          },
        },
      });

      if (this.value) {
        Redactor(this.editorElement, 'source.setCode', this.value);
      }
    },

    positionAirToolbar() {
      const wrapper = this.airToolbarEl || this.$el.querySelector('.redactor-toolbar-wrapper-air');
      if (!wrapper) {
        return;
      }

      if (wrapper.parentElement !== document.body) {
        document.body.appendChild(wrapper);
      }
      this.airToolbarEl = wrapper;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return;
      }

      const selectionRect = selection.getRangeAt(0).getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();
      const margin = 8;

      let top = selectionRect.bottom + margin;
      if (top + wrapperRect.height > window.innerHeight) {
        top = Math.max(margin, selectionRect.top - wrapperRect.height - margin);
      }

      let left = Math.min(selectionRect.left, window.innerWidth - wrapperRect.width - margin);
      left = Math.max(margin, left);

      wrapper.style.position = 'fixed';
      wrapper.style.top = `${top}px`;
      wrapper.style.left = `${left}px`;
      wrapper.style.zIndex = '1100';
    },

    patchListDropdown(dropdown) {
      const el = dropdown && typeof dropdown.get === 'function' ? dropdown.get() : null;
      if (!el || dropdown.name !== 'lists') {
        return;
      }

      const currentLi = this.getSelectionListItem();

      ['outdent', 'indent'].forEach((name) => {
        const original = el.querySelector(`.redactor-dropdown-item-${name}`);
        if (!original) {
          return;
        }

        const item = original.cloneNode(true);
        original.replaceWith(item);

        const enabled =
          name === 'indent'
            ? !!(currentLi && currentLi.previousElementSibling && currentLi.previousElementSibling.tagName === 'LI')
            : !!currentLi;
        item.classList.toggle('redactor-dropdown-item-disabled', !enabled);

        item.addEventListener('click', (e) => {
          e.preventDefault();
          if (item.classList.contains('redactor-dropdown-item-disabled')) {
            return;
          }

          const selection = window.getSelection();
          if (selection && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
          }

          Redactor(this.editorElement, `module.list.${name}`);
        });
      });
    },

    getSelectionListItem() {
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) {
        return null;
      }

      let node = selection.getRangeAt(0).startContainer;
      if (node.nodeType !== Node.ELEMENT_NODE) {
        node = node.parentElement;
      }

      const li = node ? node.closest('li') : null;
      return li && this.$el.contains(li) ? li : null;
    },

    positionDropdown(dropdown, btn) {
      const el = dropdown && typeof dropdown.get === 'function' ? dropdown.get() : null;
      const btnEl = btn && typeof btn.get === 'function' ? btn.get() : null;
      if (!el || !btnEl) {
        return;
      }

      const margin = 8;
      el.style.maxHeight = 'none';

      const btnRect = btnEl.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();

      let top = btnRect.bottom + 2;
      if (top + elRect.height > window.innerHeight - margin) {
        top = Math.max(margin, btnRect.top - elRect.height - 2);
      }

      let left = Math.min(btnRect.left, window.innerWidth - elRect.width - margin);
      left = Math.max(margin, left);

      el.style.position = 'fixed';
      el.style.top = `${top}px`;
      el.style.left = `${left}px`;
      el.style.maxHeight = `${window.innerHeight - margin * 2}px`;
    },
  },
  watch: {
    handle_type(new_val) {
      this.buildRedactor();
    },
    model_type(new_val) {
      this.buildRedactor();
    },
    model_uuid(new_val) {
      this.buildRedactor();
    },
  },
};
